/** Pieces every screen uses (Sprint 103.0). */

import { createContext, useContext, type ReactNode } from 'react';
import type { Language, Translate } from '../i18n/i18n.ts';
import type { Place } from '../model/sky.ts';

export interface Words {
  readonly t: Translate;
  readonly language: Language;
  readonly setLanguage: (language: Language) => void;
}

export const WordsContext = createContext<Words | undefined>(undefined);

export function useWords(): Words {
  const words = useContext(WordsContext);
  if (words === undefined) throw new Error('useWords outside WordsContext');
  return words;
}

/** What a place is called on screen: its address, or how it was chosen (Sprint 103.1). */
export function placeName(place: Place, t: Translate): string {
  if (place.label !== undefined) return place.label;
  return place.source === 'device' ? t('place.device') : t('place.manual');
}

export function LanguageSwitch(): ReactNode {
  const { t, language, setLanguage } = useWords();
  return (
    <div className="seg" role="group" aria-label={t('language.label')}>
      <button type="button" aria-pressed={language === 'fr'} onClick={() => setLanguage('fr')}>
        FR
      </button>
      <button type="button" aria-pressed={language === 'en'} onClick={() => setLanguage('en')}>
        EN
      </button>
    </div>
  );
}

export function BackButton({ onClick }: { readonly onClick: () => void }): ReactNode {
  const { t } = useWords();
  return (
    <button
      type="button"
      className="btn btn--quiet"
      style={{ marginLeft: '-0.5rem', padding: '0 0.5rem' }}
      onClick={onClick}
    >
      <Icon name="back" />
      {t('common.back')}
    </button>
  );
}

export function Notice({
  tone = 'warning',
  title,
  children,
  action,
  onDismiss
}: {
  readonly tone?: 'warning' | 'danger';
  readonly title?: string;
  readonly children: ReactNode;
  readonly action?: { readonly label: string; readonly onClick: () => void };
  readonly onDismiss?: () => void;
}): ReactNode {
  const { t } = useWords();
  return (
    <div role="status" className={tone === 'danger' ? 'notice notice--danger' : 'notice'}>
      <Icon name="warning" />
      <div>
        {title === undefined ? null : <strong>{title}</strong>}
        <span>{children}</span>
        {action === undefined ? null : (
          <div>
            <button type="button" className="btn btn--quiet" onClick={action.onClick}>
              {action.label}
            </button>
          </div>
        )}
      </div>
      {onDismiss === undefined ? null : (
        <button
          type="button"
          className="notice__close"
          aria-label={t('common.close')}
          onClick={onDismiss}
        >
          <Icon name="close" size={16} />
        </button>
      )}
    </div>
  );
}

const PATHS = {
  back: <path d="M15 5l-7 7 7 7" />,
  close: <path d="M6 6l12 12M18 6L6 18" />,
  camera: (
    <>
      <path d="M4 8h3l2-3h6l2 3h3v11H4z" />
      <circle cx="12" cy="13" r="3.5" />
    </>
  ),
  pin: (
    <>
      <path d="M12 21s-6.5-6.2-6.5-11a6.5 6.5 0 0 1 13 0c0 4.8-6.5 11-6.5 11z" />
      <circle cx="12" cy="10" r="2.4" />
    </>
  ),
  compass: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M15.5 8.5l-2 5-5 2 2-5z" />
    </>
  ),
  lock: (
    <>
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v6M12 7.5v.5" />
    </>
  ),
  warning: (
    <>
      <path d="M12 3l10 18H2z" />
      <path d="M12 10v5M12 18v.5" />
    </>
  ),
  calendar: (
    <>
      <rect x="4" y="5" width="16" height="15" rx="2" />
      <path d="M4 10h16M9 3v4M15 3v4" />
    </>
  ),
  sunrise: <path d="M3 18h18M7 18a5 5 0 0 1 10 0M12 4v4M9.5 6.5 12 4l2.5 2.5" />,
  sunset: <path d="M3 18h18M7 18a5 5 0 0 1 10 0M12 8V4M9.5 5.5 12 8l2.5-2.5" />,
  noon: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="13" r="8" />
      <path d="M12 9v4l2.5 2.5M10 2h4" />
    </>
  ),
  drag: <path d="M5 12h14M8 9l-3 3 3 3M16 9l3 3-3 3" />,
  search: (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M16 16l4.5 4.5" />
    </>
  ),
  locate: (
    <>
      <circle cx="12" cy="12" r="3" />
      <circle cx="12" cy="12" r="7" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
    </>
  ),
  eight: (
    <>
      <path d="M7 12c0-3 2-5 5-5s5 2 5 5-2 5-5 5-5-2-5-5z" />
      <path d="M12 7c-1.5-2-4-2.5-5.5-1S5 9 7 10.5" />
    </>
  )
} as const;

export type IconName = keyof typeof PATHS;

export function Icon({
  name,
  size = 20
}: {
  readonly name: IconName;
  readonly size?: number;
}): ReactNode {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {PATHS[name]}
    </svg>
  );
}

export function PlayIcon({ playing }: { readonly playing: boolean }): ReactNode {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      {playing ? <path d="M7 5h3.5v14H7zM13.5 5H17v14h-3.5z" /> : <path d="M7 4.5v15l12-7.5z" />}
    </svg>
  );
}
